import { useQuery } from "@apollo/client";
import moment from "moment";
import {
  GetComplianceProgramMyTimelineQuery,
  GetComplianceProgramMyTimelineQueryVariables,
  MyTimelineStepState,
} from "types/graphql-schema";

import Loading from "@components/loading/loading";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import { classNames } from "@helpers/css";

import getComplianceProgramMyTimelineQuery from "../graphql/get-compliance-program-my-timeline-query";
import { useProgramsSection } from "../programs-section-context";

type MyStep = NonNullable<
  GetComplianceProgramMyTimelineQuery["complianceProgram"]
>["myTimeline"]["steps"][number];

/** "by 8 Oct" for a step, "28 Sep" for the kickoff, which happens on its day. */
const myStepDate = (step: Pick<MyStep, "date" | "step">) => {
  if (!step.date) {
    return "—";
  }
  const day = moment(step.date).format("D MMM");
  return step.step ? `by ${day}` : day;
};

/**
 * The Timeline's You view: the steps the reader does and the steps about them,
 * with dates and who does each one. Never a count, a skipped person or anybody
 * else's results: the backend only ever describes the reader.
 */
const ComplianceProgramMyTimeline = ({
  complianceProgramId,
}: {
  complianceProgramId: number;
}) => {
  const { noun } = useProgramsSection();
  const { data, loading, error } = useQuery<
    GetComplianceProgramMyTimelineQuery,
    GetComplianceProgramMyTimelineQueryVariables
  >(getComplianceProgramMyTimelineQuery, {
    fetchPolicy: "cache-and-network",
    variables: { complianceProgramId },
  });

  const timeline = data?.complianceProgram?.myTimeline;
  if (!timeline) {
    return (
      <div className="text-sm text-gray-500">
        {loading ? (
          <Loading size="5" />
        ) : error ? (
          "Your steps could not be loaded. Reload the page to try again."
        ) : null}
      </div>
    );
  }

  return (
    <div className="flex max-w-4xl flex-col gap-3">
      {timeline.steps.length === 0 ? (
        <div className="text-sm text-gray-500">
          You have no steps in this {noun.toLowerCase()}.
        </div>
      ) : (
        <table className="w-full text-sm border rounded-lg overflow-hidden border-separate border-spacing-0">
          <thead>
            <tr className="bg-gray-50 text-xs font-medium uppercase tracking-wide text-gray-500 text-left">
              <th className="px-4 py-2 font-medium w-32">Date</th>
              <th className="px-4 py-2 font-medium">What happens</th>
              <th className="px-4 py-2 font-medium">Who</th>
            </tr>
          </thead>
          <tbody>
            {timeline.steps.map((step) => {
              const done = step.state === MyTimelineStepState.Done;
              const current = step.state === MyTimelineStepState.Current;
              return (
                <tr
                  key={`${step.step ?? "kickoff"}-${step.label}`}
                  className={classNames(current && "bg-blue-50")}
                  aria-current={current ? "step" : undefined}
                >
                  <td
                    className={classNames(
                      "px-4 py-3 border-t whitespace-nowrap",
                      done ? "text-gray-400" : "text-gray-600",
                    )}
                  >
                    {myStepDate(step)}
                  </td>
                  <td className="px-4 py-3 border-t">
                    <div className="flex flex-wrap items-center gap-2">
                      {done && (
                        <TopicflowIcon
                          type="checkCircle"
                          className="h-4 w-4 shrink-0 text-green-600"
                          aria-label="Done"
                        />
                      )}
                      <span
                        className={classNames(
                          "font-medium",
                          done ? "text-gray-500" : "text-gray-900",
                        )}
                      >
                        {step.label}
                      </span>
                      {current && (
                        <span className="rounded bg-blue-100 px-2 py-px text-xs font-semibold text-blue-800">
                          Your next step
                        </span>
                      )}
                    </div>
                  </td>
                  <td
                    className={classNames(
                      "px-4 py-3 border-t",
                      done ? "text-gray-400" : "text-gray-600",
                    )}
                  >
                    {step.who}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {timeline.notes.map((note) => (
        <p key={note} className="text-sm text-gray-600">
          {note}
        </p>
      ))}
    </div>
  );
};

export default ComplianceProgramMyTimeline;
